'use client';

import * as Dialog from '@radix-ui/react-dialog';
import clsx from 'clsx';
import { CloseType2Icon } from '@fastfive-dev/fastfive-design-system/icons/action';
import { Button } from '@/publishing/components/ui/Button';
import { getPublishingImage } from '@/shared/utils/publishingAsset';
import { GTM_EVENT } from '@/shared/constants/gtm';
import { gtmClick } from '@/shared/utils/gtm';
import {
  useDismissExitIntent,
  useExitIntentPopup,
  type ExitIntentConfig,
} from './ExitIntentDialog';

export const EXIT_INTENT_CONFIG: ExitIntentConfig = {
  /** path 지정 제외, 하위 경로까지 */
  excludeExact: ['/broker', '/contact-walkin', '/contact-outbound', '/global-eng', '/login', '/signup', '/lounge-pass', '/find-id', '/reset-password', '/privacy-policy'],
  /** 단어 포함 제외 */
  excludeContains: ['submit', 'ff-partners', 'mypage'],

  /**
   * path 포함 → 해당 팝업 (label, href, bgImage, cta, close 수정)
   *
   * CTA / 닫기 스타일. type 기본 black, outline은 채움 그대로 + 보더
   * 1. CTA white, 닫기 white
   *    cta: { type: 'white' }, close: { type: 'white' }
   * 2. CTA black, 닫기 black
   *    cta: { type: 'black' }, close: { type: 'black' }
   * 3. CTA white, 닫기 black
   *    cta: { type: 'white' }, close: { type: 'black' }
   * 4. CTA black, 닫기 white
   *    cta: { type: 'black' }, close: { type: 'white' }
   * 5. CTA outline white - 흰 배경, 텍스트 검정, 보더 gray-400
   *    cta: { type: 'white', outline: true }
   * 6. CTA outline black - 검정 배경, 텍스트 흰색, 보더 gray-600
   *    cta: { type: 'black', outline: true }
   *
   * 게시기간 (KST 기준):
   * - startDate / endDate 직접 지정 시 우선 적용 (시작일은 노출 판정에 쓰지 않음)
   * - 미지정 시 href가 HUB_EVENTS에 있으면 해당 period.end 자동 연동
   *
   * @example startDate: '2026-06-10', endDate: '2026-06-30 18:00'
   */
  include: [
    {
      id: 'estimate',
      paths: ['/event/ff-secret10thw-contact','/event/ff-today-contact','/event/ff-fiveday-contact', '/event/ff-rentfree-contact'],
      label: '무료 견적 받기',
      href: '/estimate-new',
      bgImage: 'popup/popup-estimate.jpg',
      imageAlt: '잠깐! 질문 5개면 끝나요. 30초만에 확인하는 패파 사무실 최저가',
      className: '_estimate',
      startDate: '2026-01-01', // 작성 안해도 상시 노출이지만 명시적 작성 권장
      endDate: '2099-12-31' 
    },
  ],

  /** 그 외 전체 → default 팝업 (href가 HUB_EVENTS와 일치하면 period 자동 연동) */
  default: {
    id: 'promo',
    label: '무조건 0원 바로가기',
    href: '/event/ff-rentfree-contact',
    bgImage: 'popup/popup-rentfree.jpg',
    imageAlt: '계약 기간 절반 무조건 0원',
    className: '_promo',
    cta: { type: 'white', outline: false },
    close: { type: 'white' },
    //startDate: '2026-06-10',
    //endDate: '2026-06-30 18:00'
  },
};

export function ExitIntentDialogContent() {
  const popup = useExitIntentPopup();
  const dismiss = useDismissExitIntent();
  if (!popup) return null;

  const ctaType = popup.cta?.type ?? 'black';
  const closeType = popup.close?.type ?? 'black';
  const bgUrl = getPublishingImage(popup.bgImage);

  return (
    <div
      className={`relative flex h-448 min-w-[320px] w-full max-w-400 p-24 flex-col items-center justify-center overflow-hidden rounded-[16px] bg-gray-200 p-24 shadow-[0_4px_24px_0_rgba(0,0,0,0.08)]${popup.className ? ` ${popup.className}` : ''}`}
      style={{
        backgroundImage: `url(${bgUrl})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      <Dialog.Close
        className={clsx(
          'absolute right-16 top-16 z-10 flex items-center justify-center rounded-full',
          closeType === 'white' ? 'bg-gray-100/5' : 'bg-gray-900/70',
        )}
      >
        <CloseType2Icon size={32} color="#fff" />
      </Dialog.Close>

      <div className="flex-1" aria-hidden />

      <Button
        href={popup.href}
        size="large"
        width="100%"
        type={ctaType}
        outline={popup.cta?.outline}
        onClick={dismiss}
        {...gtmClick(GTM_EVENT.EXIT_POPUP, popup.label, popup.href)}
        // icon={{ node: <Icon name={ArrowChevron} direction="right" size={24} />, position: 'right' }}
      >
        {popup.label}
      </Button>
    </div>
  );
}
